import type { ChangelogEntry } from '../types/changelog.types'

/**
 * Changelog entries for What's New feature
 *
 * MOST RECENT FIRST - Add new entries to the top of the array.
 * Version format: YY.M.patch (e.g., "26.2.1")
 *
 * Each entry appears in:
 * 1. What's New banner (if user hasn't seen this version)
 * 2. What's New page (grouped by month)
 */
export const CHANGELOG: ChangelogEntry[] = [
  // v26.6.1 - Deal Workbench + Model Upgrade
  {
    version: '26.6.1',
    date: '2026-06-19',
    title: 'Deal Workbench + Model Upgrade',
    highlights: [
      'Deal Workbench — ask, plan, and run agents on a deal in one interactive workspace',
      'Upgraded to the latest AI models (Claude Opus 4.8 + Sonnet 4.6) for sharper, faster analysis',
      'Fixed document upload failing during processing',
    ],
    features: [
      {
        title: 'Deal Workbench',
        description:
          'A workspace for each deal: Ask for quick cited answers, Plan multi-step research, and run Agent mode to re-run dimensions — with side-by-side score diffs and full history.',
        category: 'feature',
      },
      {
        title: 'Latest AI models',
        description:
          'Migrated every analysis path to the newest same-cost models — Claude Opus 4.8, Sonnet 4.6, Haiku 4.5, plus refreshed OpenAI and Gemini — for better long-context reasoning at the same price.',
        category: 'improvement',
      },
      {
        title: 'Document upload fix',
        description:
          'Uploads were failing during processing because an underlying AI model had been retired. Document-to-markdown conversion now runs on the current model and works again.',
        category: 'fix',
      },
    ],
  },
  // v26.3.2 - Custom Scoring Framework
  {
    version: '26.3.2',
    date: '2026-03-23',
    title: 'Custom Scoring Framework',
    highlights: [
      "Configure your fund's scoring criteria — sectors, stages, signals, and deal breakers",
      'Each deal locks its scoring criteria at analysis time for consistent evaluation',
    ],
    features: [
      {
        title: 'Fund Profile & Sector Scoring',
        description:
          "Define your fund's investment preferences — sectors, stages, check size, thesis — and customize per-sector scoring metrics, red flags, founder signals, and benchmark questions.",
        category: 'feature',
        showMeWhere: '/scoring',
      },
      {
        title: 'Stage Weights',
        description:
          'Adjust IMPACT-X dimension weights per investment stage. Seed deals emphasize Innovators and Product, while Series A shifts weight toward Traction and Commerce.',
        category: 'feature',
        showMeWhere: '/scoring',
      },
      {
        title: 'Signals & Deal Breakers',
        description:
          'Set deal breaker rules that auto-flag critical issues, define signature questions for every deal, and configure green/yellow flags for pattern recognition.',
        category: 'feature',
        showMeWhere: '/scoring',
      },
      {
        title: 'Frozen Scoring Criteria',
        description:
          'When Sieve analyzes a deal, it snapshots your current scoring configuration. This ensures consistent evaluation even if you update your criteria later.',
        category: 'improvement',
      },
    ],
  },
  // v26.3.1 - Smart Refresh & Live Progress
  {
    version: '26.3.1',
    date: '2026-02-27',
    title: 'Smart Refresh & Live Progress',
    highlights: [
      'Lightweight refresh when new documents are added — no full re-run needed',
      'Sieve recommends the best action based on what changed',
      'Real-time progress tracking as each dimension is analyzed',
    ],
    features: [
      {
        title: 'Refresh Analysis',
        description:
          'When you add new documents to the data room, Sieve detects the change and offers a lightweight refresh — updating scores and findings without re-running the full analysis pipeline.',
        category: 'feature',
        showMeWhere: '/deals',
      },
      {
        title: 'Contextual Recommendations',
        description:
          'Sieve recommends a quick update for 1-3 new documents or a full re-run for larger changes. The alternative action is always available if you prefer.',
        category: 'feature',
      },
      {
        title: 'Live Analysis Progress',
        description:
          'Watch Sieve work in real-time — see progress through each IMPACT-X dimension, score recalibration, and evidence synthesis as your analysis updates.',
        category: 'improvement',
      },
      {
        title: 'Admin Command Center',
        description:
          'Platform administrators can monitor organization activity, user engagement, and system health from a dedicated admin panel with real-time metrics.',
        category: 'feature',
        showMeWhere: '/admin',
      },
    ],
  },
  // v26.2.7 - Notion Integration
  {
    version: '26.2.7',
    date: '2026-02-18',
    title: 'Notion Integration',
    highlights: [
      'Connect your Notion workspace to import deal data',
      'Export investment memos directly to Notion',
      'Browse and select Notion pages or database rows',
    ],
    features: [
      {
        title: 'Import from Notion',
        description:
          'Connect your Notion workspace and import pages or database rows into your deal data room. Perfect for VCs who manage their deal pipeline in Notion.',
        category: 'feature',
        showMeWhere: '/settings?tab=integrations',
      },
      {
        title: 'Export Memos to Notion',
        description:
          'Export investment memos to any Notion page with one click. Sieve converts the full memo into formatted Notion blocks — headings, tables, and all.',
        category: 'feature',
      },
      {
        title: 'Secure OAuth Connection',
        description:
          'Admin-only workspace connection with encrypted token storage. Connect once and your entire team can import and export.',
        category: 'improvement',
      },
    ],
  },
  // v26.2.6 - docusend & URL Import
  {
    version: '26.2.6',
    date: '2026-02-10',
    title: 'docusend & URL Import',
    highlights: [
      'Paste a docusend link to create a deal from a pitch deck',
      'Supports any URL — docusend, website, or direct link',
      'Email gate handled automatically for docusend',
    ],
    features: [
      {
        title: 'docusend Import',
        description:
          'Paste a docusend link with your email and Sieve extracts the full pitch deck — including image-only slides via OCR.',
        category: 'feature',
        showMeWhere: '/deals',
      },
      {
        title: 'URL Import',
        description:
          'Import pitch decks from any URL. The new "Paste Link" tab sits alongside the existing file upload.',
        category: 'feature',
        showMeWhere: '/deals',
      },
    ],
  },
  // v26.2.5 - Document Intelligence & Citations
  {
    version: '26.2.5',
    date: '2026-02-10',
    title: 'Document Intelligence & Citations',
    highlights: [
      'Upload DOCX, PPTX, and images to Data Room',
      'Inline source citations in Ask Sieve responses',
      'Click-to-preview source documents',
    ],
    features: [
      {
        title: 'Multi-Format Data Room',
        description:
          'Upload Word documents, PowerPoint presentations, screenshots, and images alongside PDFs. Sieve extracts and analyzes content from all formats.',
        category: 'feature',
      },
      {
        title: 'Inline Citations',
        description:
          'Ask Sieve responses now include inline source citations — click any reference to see exactly where the information came from.',
        category: 'feature',
      },
      {
        title: 'Source Preview',
        description:
          'Preview source documents and evidence without leaving the deal page. Click any citation or evidence link to see the original content in context.',
        category: 'feature',
      },
      {
        title: 'Diligence UI Refinements',
        description:
          'Improved dimension cards, signal evidence modals, and knowledge panel for a cleaner analysis experience.',
        category: 'improvement',
      },
    ],
  },
  // v26.2.4 - Chinese Language Support
  {
    version: '26.2.4',
    date: '2026-02-10',
    title: 'Chinese Language Support',
    highlights: [
      'Full Simplified & Traditional Chinese analysis',
      'Auto-detects pitch deck language',
      'VC terminology preserved across languages',
    ],
    features: [
      {
        title: 'Automatic Language Detection',
        description:
          'Upload a Chinese pitch deck and Sieve automatically detects Simplified Chinese, Traditional Chinese, or Hong Kong Chinese — no configuration needed.',
        category: 'feature',
      },
      {
        title: 'Full Pipeline Localization',
        description:
          'All 7 IMPACT-X dimensions, cross-checker verification, executive summary, and risk analysis output in the detected language with locale-aware formatting.',
        category: 'feature',
      },
      {
        title: 'VC Domain Terminology',
        description:
          'Purpose-built financial and VC terminology for Chinese markets — TAM/SAM/SOM, unit economics, and founder evaluation use industry-standard Chinese terms.',
        category: 'improvement',
      },
    ],
  },
  // v26.2.3 - Public API & Developer Access
  {
    version: '26.2.3',
    date: '2026-02-10',
    title: 'Public API & Developer Access',
    highlights: [
      'Programmatic access via API keys',
      'Public screening endpoints',
      'Tier-based rate limiting',
    ],
    features: [
      {
        title: 'API Key Management',
        description:
          'Create, manage, and revoke API keys from Settings. Keys use SHA-256 hashing with sk_live_ prefix for easy identification.',
        category: 'feature',
        showMeWhere: '/settings',
      },
      {
        title: 'Public Screening API',
        description:
          'Submit deals, check analysis status, and retrieve summaries programmatically. Integrate Sieve into your existing deal flow tools and CRM.',
        category: 'feature',
      },
      {
        title: 'Tier-Based Rate Limiting',
        description:
          'Free tier includes 2 screenings per month. Pro tier unlocks unlimited API access with persistent usage tracking.',
        category: 'feature',
      },
    ],
  },
  // v26.2.2 - Enhanced Deal Experience
  {
    version: '26.2.2',
    date: '2026-02-10',
    title: 'Enhanced Deal Experience',
    highlights: [
      'At-a-Glance deal overview for faster decisions',
      'Edit and manage deals directly from your pipeline',
      'Sieve now shows you its tools in real-time',
    ],
    features: [
      {
        title: 'At-a-Glance Overview',
        description:
          'See the most important deal signals at a glance with a new collapsible summary section. Expand into a full analysis modal for deeper exploration.',
        category: 'feature',
        showMeWhere: '/deals',
      },
      {
        title: 'Edit & Delete Deals',
        description:
          'Manage your deal pipeline with inline editing and deletion. Update deal details or remove outdated entries directly from the deals list.',
        category: 'feature',
        showMeWhere: '/deals',
      },
      {
        title: 'Ask Sieve — Tool Transparency',
        description:
          'Sieve now shows you exactly which tools it\'s using in real-time — web search, chart generation, financial analysis — so you always know how answers are built.',
        category: 'feature',
      },
      {
        title: 'Financial Charts in Chat',
        description:
          'Ask Sieve about revenue, growth, or burn rate and get interactive charts with multi-series support, zoom, and smart axis formatting.',
        category: 'feature',
      },
      {
        title: 'Investment Memo PDF Export',
        description:
          'Export investment memos as polished PDFs with financial metrics, team analysis, and evidence-backed scoring — ready for IC review.',
        category: 'feature',
      },
      {
        title: 'Data Room Uploads',
        description:
          'Upload Markdown and JSON files to the data room alongside existing document types.',
        category: 'improvement',
      },
    ],
  },
  // v26.2.1 - Sieve Intelligence Engine 2.0
  {
    version: '26.2.1',
    date: '2026-02-07',
    title: 'Sieve Intelligence Engine 2.0',
    highlights: [
      'Next-gen adaptive reasoning calibrated for VC diligence',
      'Cross-dimensional verification with proactive discovery',
      'Faster API responses with async infrastructure',
    ],
    features: [
      {
        title: 'Adaptive Reasoning Engine',
        description:
          'Upgraded to next-generation AI with adaptive thinking calibrated per dimension — deeper reasoning for financial models, sharper pattern matching for founder evaluation, and maximum-effort cross-validation.',
        category: 'feature',
      },
      {
        title: 'Proactive Discovery',
        description:
          'Sieve now proactively searches for company red flags, founder background signals, and recent news sentiment — surfacing risks you didn\'t think to ask about.',
        category: 'feature',
      },
      {
        title: 'Cross-Checker Verification',
        description:
          'Every signal is fact-checked against live sources. Verified, disputed, or gap-filled — you see exactly what Sieve could and couldn\'t confirm.',
        category: 'improvement',
      },
      {
        title: 'Faster Performance',
        description:
          'Complete async infrastructure migration with smart caching, optimized database queries, and connection pooling.',
        category: 'improvement',
      },
      {
        title: 'Prompt Caching',
        description:
          'Intelligent prompt caching reduces analysis time and cost for repeated patterns across your deal pipeline.',
        category: 'improvement',
      },
    ],
  },
  // v26.1.3 - Bug Reporting
  {
    version: '26.1.3',
    date: '2026-01-30',
    title: 'In-App Bug Reporting',
    highlights: [
      'Report issues directly from the app',
    ],
    features: [
      {
        title: 'Report Bugs from Anywhere',
        description:
          'Click the bug icon in the sidebar to report issues with screenshots and file attachments. Your feedback helps us improve Sieve faster.',
        category: 'feature',
      },
      {
        title: 'Smart Context',
        description:
          'When reporting from a deal page, we automatically include the deal context to help us investigate faster.',
        category: 'improvement',
      },
    ],
  },
  // v26.1.2 - Scale & Reliability
  {
    version: '26.1.2',
    date: '2026-01-28',
    title: 'Built for Scale',
    highlights: [
      'Handle multiple deals running simultaneously',
      'Faster analysis with smart caching',
      'More reliable connections',
    ],
    features: [
      {
        title: 'Run Multiple Analyses',
        description:
          'Improved infrastructure to handle multiple Quick Screens and Deep Dives running at the same time without slowdowns.',
        category: 'improvement',
      },
      {
        title: 'Faster Startup',
        description:
          'Smart caching means frequently accessed data loads instantly.',
        category: 'improvement',
      },
      {
        title: 'Reliability Improvements',
        description:
          'Better connection management ensures consistent performance even under heavy usage.',
        category: 'fix',
      },
    ],
  },
  // v26.1.1 - Analysis Enhancements
  {
    version: '26.1.1',
    date: '2026-01-27',
    title: 'Smarter Analysis',
    highlights: [
      'Revenue projections with visual charts',
      'Signals organized by category',
      'Better LinkedIn profile matching',
    ],
    features: [
      {
        title: 'ARR Projections & Charts',
        description:
          'Automatic extraction of Annual Recurring Revenue projections from pitch decks, displayed with interactive charts.',
        category: 'feature',
        showMeWhere: '/deals',
      },
      {
        title: 'Signals Grouped by Category',
        description:
          'Dimension signals are now organized by category (Company Info, Business Model, etc.) for easier navigation.',
        category: 'improvement',
      },
      {
        title: 'Missing vs Incomplete Data',
        description:
          'Data gaps now split into separate tabs so you can clearly see what information is missing versus partially available.',
        category: 'improvement',
      },
      {
        title: 'Better Founder Matching',
        description:
          'Improved LinkedIn profile matching for more accurate founder identification and background verification.',
        category: 'improvement',
      },
    ],
  },
  // v26.1.0 - Initial Release
  {
    version: '26.1',
    date: '2026-01-25',
    title: 'Welcome to Sieve',
    highlights: [
      'AI-powered deal analysis with real-time Quick Screen',
      'Comprehensive Deep Diligence reports',
      'Team collaboration and document management',
    ],
    features: [
      {
        title: 'Quick Screen Analysis',
        description:
          'Get instant insights on any startup pitch deck. Sieve analyzes market opportunity, team strength, traction, and more in under 5 minutes.',
        category: 'feature',
        showMeWhere: '/deals',
      },
      {
        title: 'Deep Diligence Reports',
        description:
          'Comprehensive investment memos with evidence-backed analysis. Every claim is verified and sourced, with clear indicators of what couldn\'t be verified.',
        category: 'feature',
        showMeWhere: '/deals',
      },
      {
        title: 'Team Collaboration',
        description:
          'Invite team members, assign roles, and collaborate on deal analysis. Share insights and maintain a single source of truth for investment decisions.',
        category: 'feature',
        showMeWhere: '/settings/team',
      },
    ],
  },
]
